import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Box,
  ButtonBase,
  CircularProgress,
  IconButton,
  InputBase,
  Paper,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { format, parseISO } from "date-fns";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { SearchMessageContext } from "../../context/SearchMessage/SearchMessageContext";

const MESSAGES_PAGE_SIZE = 20;

const MEDIA_ICONS = {
  image: ImageOutlinedIcon,
  sticker: ImageOutlinedIcon,
  video: VideocamOutlinedIcon,
  document: InsertDriveFileOutlinedIcon,
  application: InsertDriveFileOutlinedIcon,
};

const renderSnippet = (body, term) => {
  const idx = body.toLowerCase().indexOf(term.toLowerCase());
  if (idx === -1) return body;

  const start = idx > 30 ? idx - 20 : 0;
  const prefix = start > 0 ? "…" : "";

  return (
    <>
      {prefix}
      {body.slice(start, idx)}
      <strong>{body.slice(idx, idx + term.length)}</strong>
      {body.slice(idx + term.length)}
    </>
  );
};

const TicketMessagesSearch = ({ ticketId, onClose }) => {
  const theme = useTheme();
  const { requestJump } = useContext(SearchMessageContext);

  const [searchParam, setSearchParam] = useState("");
  const [matches, setMatches] = useState([]);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [searchPage, setSearchPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [panelOpen, setPanelOpen] = useState(true);
  const currentTermRef = useRef("");

  const term = searchParam.trim();
  const showPanel = panelOpen && term.length >= 2;

  useEffect(() => {
    currentTermRef.current = term;

    if (term.length < 2) {
      setMatches([]);
      setCount(0);
      setHasMore(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get(`/messages/${ticketId}/search`, {
          params: { searchParam: term, pageNumber: 1 },
        });
        if (currentTermRef.current !== term) return;
        setMatches(data.matches);
        setCount(data.count);
        setHasMore(data.hasMore);
        setSearchPage(1);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [term, ticketId]);

  const handleLoadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const { data } = await api.get(`/messages/${ticketId}/search`, {
        params: { searchParam: term, pageNumber: searchPage + 1 },
      });
      if (currentTermRef.current !== term) return;
      setMatches((prev) => [...prev, ...data.matches]);
      setCount(data.count);
      setHasMore(data.hasMore);
      setSearchPage((prev) => prev + 1);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleResultClick = (match) => {
    // Collapse the dropdown so the agent can see the highlighted message;
    // typing or refocusing the input reopens it.
    setPanelOpen(false);
    requestJump(match.id, Math.ceil(match.position / MESSAGES_PAGE_SIZE));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") onClose();
  };

  const senderLabel = (match) =>
    match.fromMe ? match.user?.name ?? "You" : match.contact?.name ?? "";

  return (
    <Box
      sx={{
        position: "relative",
        flexShrink: 0,
        borderBottom: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        px: 1,
        py: 0.75,
        zIndex: (t) => t.zIndex.appBar,
      }}
    >
      {/* Search input */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          px: 1.25,
          height: 34,
          borderRadius: 2,
          bgcolor:
            theme.palette.mode === "dark"
              ? alpha(theme.palette.common.white, 0.08)
              : alpha(theme.palette.common.black, 0.05),
          border: "1px solid",
          borderColor: theme.palette.primary.main,
        }}
      >
        {loading ? (
          <CircularProgress size={14} sx={{ color: "text.disabled", flexShrink: 0 }} />
        ) : (
          <SearchOutlinedIcon sx={{ fontSize: 16, color: "text.disabled", flexShrink: 0 }} />
        )}
        <InputBase
          autoFocus
          value={searchParam}
          onChange={(e) => {
            setSearchParam(e.target.value);
            setPanelOpen(true);
          }}
          onFocus={() => setPanelOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={i18n.t("ticketMessagesSearch.placeholder")}
          fullWidth
          sx={{
            fontSize: 13,
            "& input": {
              p: 0,
              color: "text.primary",
              "&::placeholder": { color: "text.disabled", opacity: 1 },
            },
          }}
        />
        {count > 0 && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
          >
            {i18n.t("ticketMessagesSearch.results", { count })}
          </Typography>
        )}
        <IconButton size="small" onClick={onClose} sx={{ flexShrink: 0, p: 0.25 }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* Results panel */}
      {showPanel && !loading && (
        <Paper
          elevation={8}
          sx={{
            position: "absolute",
            top: "calc(100% + 2px)",
            left: 8,
            right: 8,
            zIndex: (t) => t.zIndex.modal,
            borderRadius: 2,
            overflowY: "auto",
            maxHeight: 320,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          {matches.length === 0 ? (
            <Box sx={{ px: 2, py: 2.5, textAlign: "center" }}>
              <Typography variant="body2" color="text.disabled">
                {i18n.t("ticketMessagesSearch.noResults")}
              </Typography>
            </Box>
          ) : (
            <>
              {matches.map((match) => {
                const MediaIcon = MEDIA_ICONS[match.mediaType];
                return (
                  <Box
                    key={match.id}
                    onClick={() => handleResultClick(match)}
                    sx={{
                      px: 2,
                      py: 1,
                      cursor: "pointer",
                      borderRadius: 1,
                      mx: 0.5,
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      noWrap
                      display="block"
                    >
                      {[senderLabel(match), format(parseISO(match.createdAt), "MMM d, HH:mm")]
                        .filter(Boolean)
                        .join(" · ")}
                    </Typography>
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      {MediaIcon && (
                        <MediaIcon sx={{ fontSize: 14, color: "text.disabled", flexShrink: 0 }} />
                      )}
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                        {renderSnippet(match.body, term)}
                      </span>
                    </Typography>
                  </Box>
                );
              })}
              {hasMore && (
                <ButtonBase
                  onClick={handleLoadMore}
                  sx={{
                    width: "100%",
                    py: 1,
                    borderTop: "1px solid",
                    borderColor: "divider",
                    fontSize: 12,
                    color: "primary.main",
                  }}
                >
                  {loadingMore ? (
                    <CircularProgress size={14} />
                  ) : (
                    i18n.t("ticketMessagesSearch.loadMore")
                  )}
                </ButtonBase>
              )}
            </>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default TicketMessagesSearch;
